import { getBackendUrl } from '@/lib/backendUrl';
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Upload, Plus, Pencil, Check, X, AlertTriangle, ShieldCheck, ShieldQuestion } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const API = getBackendUrl();

function authHeaders() {
  const t = localStorage.getItem('access_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

// Parses a date-only string (e.g. "2026-10-31") as a LOCAL date, not UTC
// midnight — new Date("2026-10-31") is parsed as UTC and can display as
// the previous day in timezones behind UTC. Always shown dd/mm/yyyy.
function formatDateOnly(isoDateStr) {
  if (!isoDateStr) return null;
  const [y, m, d] = isoDateStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-GB');
}

function StatusBadge({ item }) {
  if (item.qbo_status === 'ambiguous') {
    return <Badge className="bg-orange-500" title="A later import brought a different QBO code for this name — resolve by editing the code.">Ambiguous — check in QBO</Badge>;
  }
  if (!item.is_active) {
    return <Badge variant="secondary">Inactive</Badge>;
  }
  if (item.qbo_status === 'verified') {
    return <Badge className="bg-emerald-600">Verified active in QBO</Badge>;
  }
  return <Badge variant="secondary" className="bg-sky-100 text-sky-800">Check in QBO</Badge>;
}

export default function ReferenceListEditor({ listType, label }) {
  const [items, setItems] = useState([]);
  const [lastImport, setLastImport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [asOfDate, setAsOfDate] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({});
  const [newName, setNewName] = useState('');
  const [newCode, setNewCode] = useState('');
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/sales-invoicing/reference-lists/${listType}`, { headers: authHeaders() });
      setItems(data.items || []);
      setLastImport(data.last_import || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load reference list.');
    } finally {
      setLoading(false);
    }
  }, [listType]);

  useEffect(() => { load(); }, [load]);

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!asOfDate) {
      toast.error('Enter the extract\'s as-of date before importing.');
      e.target.value = '';
      return;
    }
    setUploading(true);
    const form = new FormData();
    form.append('file', file);
    form.append('as_of_date', asOfDate);
    try {
      const { data } = await axios.post(`${API}/api/sales-invoicing/reference-lists/${listType}/import`, form, { headers: authHeaders() });
      toast.success(data.message);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setAdding(true);
    try {
      await axios.post(`${API}/api/sales-invoicing/reference-lists/${listType}/items`, { name: newName.trim(), external_code: newCode.trim() || null }, { headers: authHeaders() });
      setNewName(''); setNewCode('');
      toast.success('Added — flagged "Check in QBO" until a human verifies it.');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not add this entry.');
    } finally {
      setAdding(false);
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditDraft({ name: item.name, external_code: item.external_code || '', is_active: item.is_active });
  };

  const saveEdit = async (id) => {
    try {
      await axios.patch(`${API}/api/sales-invoicing/reference-lists/${listType}/items/${id}`, editDraft, { headers: authHeaders() });
      setEditingId(null);
      toast.success('Updated.');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save this change.');
    }
  };

  const toggleVerified = async (item) => {
    try {
      await axios.post(`${API}/api/sales-invoicing/reference-lists/${listType}/items/${item.id}/verify`, { verified: item.qbo_status !== 'verified' }, { headers: authHeaders() });
      toast.success(item.qbo_status === 'verified' ? 'Verification cleared.' : 'Marked verified in QBO.');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update verification.');
    }
  };

  return (
    <div data-testid={`reference-list-editor-${listType}`}>
      {lastImport ? (
        <div className={`flex items-center gap-2 text-sm mb-3 px-3 py-2 rounded-md ${lastImport.stale ? 'bg-amber-50 text-amber-800 border border-amber-200' : 'bg-slate-50 text-slate-600 border border-slate-200'}`} data-testid={`reference-list-last-import-${listType}`}>
          {lastImport.stale && <AlertTriangle className="h-4 w-4 flex-shrink-0" />}
          As of {lastImport.as_of_date ? formatDateOnly(lastImport.as_of_date) : 'unknown'} (imported {new Date(lastImport.uploaded_at).toLocaleString('en-GB')}, {lastImport.filename || 'unnamed file'}) —
          {' '}{lastImport.imported_count} new, {lastImport.skipped_count} skipped
          {lastImport.stale && ` — over ${lastImport.stale_threshold_days} days old, consider re-importing`}
        </div>
      ) : (
        <div className="text-sm text-slate-500 mb-3" data-testid={`reference-list-no-import-${listType}`}>No import yet for {label.toLowerCase()} — add entries inline or import a CSV below.</div>
      )}

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <Input
          type="date"
          value={asOfDate}
          onChange={(e) => setAsOfDate(e.target.value)}
          className="w-[160px]"
          placeholder="Extract as-of date"
          data-testid={`reference-list-as-of-date-${listType}`}
        />
        <label className="inline-flex">
          <input type="file" accept=".csv" className="hidden" onChange={handleImport} data-testid={`reference-list-import-input-${listType}`} />
          <span className={`inline-flex items-center gap-2 px-3 py-2 text-sm border rounded-md ${asOfDate ? 'cursor-pointer hover:bg-slate-50' : 'opacity-50 cursor-not-allowed'}`}>
            <Upload className="h-4 w-4" /> {uploading ? 'Importing…' : `Import ${label} CSV`}
          </span>
        </label>
        <span className="text-xs text-slate-400">Set the extract's as-of date first — an import never proves current QBO availability on its own.</span>
      </div>

      <div className="flex items-center gap-2 mb-4 p-3 bg-slate-50 rounded-md border">
        <Input placeholder="Name" value={newName} onChange={(e) => setNewName(e.target.value)} className="max-w-xs" data-testid={`reference-list-new-name-${listType}`} />
        <Input placeholder="Code (optional)" value={newCode} onChange={(e) => setNewCode(e.target.value)} className="max-w-[160px]" data-testid={`reference-list-new-code-${listType}`} />
        <Button size="sm" onClick={handleAdd} disabled={adding || !newName.trim()} data-testid={`reference-list-add-btn-${listType}`}>
          <Plus className="h-4 w-4 mr-1" /> Add
        </Button>
      </div>

      <div className="overflow-x-auto">
      <Table data-testid={`reference-list-table-${listType}`}>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Code</TableHead>
            <TableHead>Source</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading && <TableRow><TableCell colSpan={5} className="text-center text-slate-500">Loading…</TableCell></TableRow>}
          {!loading && items.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-500">No {label.toLowerCase()} entries yet.</TableCell></TableRow>}
          {items.map((item) => (
            <TableRow key={item.id} data-testid={`reference-list-row-${listType}-${item.id}`}>
              {editingId === item.id ? (
                <>
                  <TableCell><Input value={editDraft.name} onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })} data-testid={`reference-list-edit-name-${item.id}`} /></TableCell>
                  <TableCell><Input value={editDraft.external_code} onChange={(e) => setEditDraft({ ...editDraft, external_code: e.target.value })} data-testid={`reference-list-edit-code-${item.id}`} /></TableCell>
                  <TableCell className="text-slate-400 text-xs">{item.source}</TableCell>
                  <TableCell>
                    <button onClick={() => setEditDraft({ ...editDraft, is_active: !editDraft.is_active })} data-testid={`reference-list-edit-active-toggle-${item.id}`}>
                      <Badge variant={editDraft.is_active ? 'default' : 'secondary'}>{editDraft.is_active ? 'Active' : 'Inactive'}</Badge>
                    </button>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" onClick={() => saveEdit(item.id)} data-testid={`reference-list-save-btn-${item.id}`}><Check className="h-4 w-4 text-green-600" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => setEditingId(null)} data-testid={`reference-list-cancel-btn-${item.id}`}><X className="h-4 w-4 text-slate-400" /></Button>
                  </TableCell>
                </>
              ) : (
                <>
                  <TableCell className="font-medium">{item.name}</TableCell>
                  <TableCell className="text-slate-500">{item.external_code || '—'}</TableCell>
                  <TableCell className="text-slate-400 text-xs capitalize">{item.source}</TableCell>
                  <TableCell data-testid={`reference-list-status-${item.id}`}><StatusBadge item={item} /></TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button
                      size="icon" variant="ghost" onClick={() => toggleVerified(item)}
                      disabled={item.qbo_status === 'ambiguous'}
                      title={item.qbo_status === 'verified' ? 'Clear QBO verification' : 'Mark verified in QBO'}
                      data-testid={`reference-list-verify-btn-${item.id}`}
                    >
                      {item.qbo_status === 'verified' ? <ShieldCheck className="h-4 w-4 text-emerald-600" /> : <ShieldQuestion className="h-4 w-4 text-slate-400" />}
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => startEdit(item)} data-testid={`reference-list-edit-btn-${item.id}`}><Pencil className="h-4 w-4" /></Button>
                  </TableCell>
                </>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      </div>
    </div>
  );
}
